import React, { useState, useRef } from 'react';
import { Div, Button, Heading, Span, P } from '@kizi/core-components';
import { IconV2 as Icon } from '@kizi/shared-components';
import { Window } from '../Window';
import { ArrowComp, ModalWithAccordionProps } from './ModalWithAccordion';
import {
  ComponentDescriptorOrArray,
  getRendererFromDescriptor,
} from '@kizi/comparator-core';

export interface ModalWithCustomContentProps extends ModalWithAccordionProps {
  content?: ComponentDescriptorOrArray;
}

export const innerAccordionItemWithLabel =
  (
    label: React.ReactNode,
    index: number,
    icon?: string,
    headerSubtext?: string,
    iconColor?: string,
  ) =>
  (isOpen: boolean) => {
    return (
      <Div
        key={`label-${index}`}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        css={{ cursor: 'pointer' }}
      >
        <Div display="flex" alignItems="center">
          {icon && <Icon name={'green-check'} size={28} />}
          <Heading as="h3" variant="H3" my={[0, 3]} fontSize={[3, 5]}>
            {label}
          </Heading>
        </Div>
        <Div display="flex">
          {headerSubtext && <Span>{headerSubtext}</Span>}
          <ArrowComp isOpen={isOpen} fill={iconColor} />
        </Div>
      </Div>
    );
  };

export const ModalWithCustomContent: React.FC<ModalWithCustomContentProps> =
  React.forwardRef<HTMLDivElement, ModalWithCustomContentProps>(
    (
      {
        buttonIcon,
        buttonIconProps,
        buttonLabel,
        buttonProps,
        title,
        content = [],
        variant = 'transparent',
        buttonSubtitle,
      },
      ref,
    ) => {
      const [show, setShow] = useState(false);
      const buttonRef = useRef<HTMLButtonElement>(null);

      return (
        <>
          <Button
            onClick={() => setShow(!show)}
            ref={buttonRef}
            display="flex"
            alignItems={buttonSubtitle ? 'flex-start' : 'center'}
            variant={variant}
            {...buttonProps}
          >
            {buttonIcon && (
              <Icon
                name={buttonIcon}
                size={22}
                mr={2}
                variant="iconLink"
                {...buttonIconProps}
              />
            )}

            {buttonSubtitle ? (
              <Div mt={'-3px'}>
                <Span fontSize={2} fontWeight={1} css={{ lineHeight: '24px' }}>
                  {buttonLabel}
                </Span>
                <P
                  fontSize={1}
                  textAlign={'start'}
                  fontWeight={0}
                  css={{ lineHeight: '16px' }}
                >
                  {buttonSubtitle}
                </P>
              </Div>
            ) : (
              <Span>{buttonLabel}</Span>
            )}
          </Button>

          <Window
            onClose={() => setShow(false)}
            modalProps={{
              show: show,
              title,
              children: (
                <Div ref={ref}>{<>{getRendererFromDescriptor(content)}</>}</Div>
              ),
            }}
            show={show}
            title={title}
            type="modal"
            showIcon={true}
          >
            {<>{getRendererFromDescriptor(content)}</>}
          </Window>
        </>
      );
    },
  );
